import React, { createContext, useContext, useState, useEffect } from "react";
import collectionsService from "../services/collectionsService";

/**
 * Collection Context
 * Provides shared state for collection data across components
 * Now includes full collections data with localStorage persistence
 */
const CollectionContext = createContext();

// localStorage key for collections data
const COLLECTIONS_STORAGE_KEY = "collections_data";

/**
 * Custom hook to use Collection Context
 * @returns {Object} Collection context value
 */
export const useCollection = () => {
  const context = useContext(CollectionContext);
  if (!context) {
    throw new Error("useCollection must be used within a CollectionProvider");
  }
  return context;
};

/**
 * Collection Provider Component
 * Wraps app to provide collection state management
 */
export const CollectionProvider = ({ children }) => {
  const [currentCollection, setCurrentCollection] = useState(null);
  const [collectionsData, setCollectionsData] = useState({
    collections: [],
    lastUpdated: null,
  });

  /**
   * Load collections data from localStorage
   */
  const loadCollectionsFromStorage = () => {
    try {
      const stored = localStorage.getItem(COLLECTIONS_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        setCollectionsData(data);
        return data;
      }
    } catch (error) {
      console.error("Error loading collections from localStorage:", error);
    }
    return { collections: [], lastUpdated: null };
  };

  /**
   * Save collections data to localStorage
   */
  const saveCollectionsToStorage = (data) => {
    try {
      localStorage.setItem(COLLECTIONS_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Error saving collections to localStorage:", error);
    }
  };

  /**
   * Initialize collections data from localStorage on mount
   */
  useEffect(() => {
    loadCollectionsFromStorage();
  }, []);

  /**
   * Set all collections data
   * @param {Array} collections - Array of collection objects
   */
  const setCollections = (collections) => {
    const data = {
      collections,
      lastUpdated: new Date().toISOString(),
    };
    setCollectionsData(data);
    saveCollectionsToStorage(data);
  };

  /**
   * Get collection data by ID
   * @param {string} collectionId - Collection ID
   * @returns {Object|null} Collection object or null
   */
  const getCollectionData = (collectionId) => {
    return (
      collectionsData.collections.find((c) => c.id === collectionId) || null
    );
  };

  /**
   * Update specific collection data
   * @param {string} collectionId - Collection ID
   * @param {Object} updatedData - Updated collection data
   */
  const updateCollectionData = (collectionId, updatedData) => {
    const updatedCollections = collectionsData.collections.map((collection) =>
      collection.id === collectionId
        ? { ...collection, ...updatedData }
        : collection
    );

    const data = {
      collections: updatedCollections,
      lastUpdated: new Date().toISOString(),
    };

    setCollectionsData(data);
    saveCollectionsToStorage(data);

    // Update current collection if it's the one being updated
    if (currentCollection && currentCollection.id === collectionId) {
      setCurrentCollection({ ...currentCollection, ...updatedData });
    }
  };

  /**
   * Fetch collection details from API and store in context
   * @param {string} collectionId - Collection ID
   * @returns {Promise<Object>} Collection details
   */
  const fetchCollectionDetails = async (collectionId) => {
    try {
      const collectionDetails = await collectionsService.getCollectionDetails(
        collectionId
      );

      // Update the collections list if this collection exists there
      const existingCollectionIndex = collectionsData.collections.findIndex(
        (c) => c.id === collectionId
      );

      let updatedCollections;
      if (existingCollectionIndex !== -1) {
        // Update existing collection in the list
        updatedCollections = collectionsData.collections.map((collection) =>
          collection.id === collectionId
            ? { ...collection, ...collectionDetails }
            : collection
        );
      } else {
        // Add new collection to the list
        updatedCollections = [
          ...collectionsData.collections,
          collectionDetails,
        ];
      }

      const data = {
        collections: updatedCollections,
        lastUpdated: new Date().toISOString(),
      };

      setCollectionsData(data);
      saveCollectionsToStorage(data);

      return collectionDetails;
    } catch (error) {
      console.error("Error fetching collection details:", error);
      throw error;
    }
  };

  /**
   * Refresh collections data (for focus-based updates)
   * @param {Array} freshCollections - Fresh collections data from API
   */
  const refreshCollections = (freshCollections) => {
    setCollections(freshCollections);
  };

  /**
   * Set the current collection data
   * @param {Object} collection - Collection data object
   */
  const setCollection = (collection) => {
    setCurrentCollection(collection);
  };

  /**
   * Get the current collection data
   * @returns {Object|null} Current collection or null
   */
  const getCurrentCollection = () => {
    return currentCollection;
  };

  /**
   * Clear the current collection data
   */
  const clearCollection = () => {
    setCurrentCollection(null);
  };

  /**
   * Check if a collection is currently set
   * @returns {boolean} True if collection exists
   */
  const hasCollection = () => {
    return !!currentCollection;
  };

  /**
   * Get collection name with fallback
   * @param {string} fallbackId - ID to use in fallback name
   * @returns {string} Collection name or fallback
   */
  const getCollectionName = (fallbackId = "") => {
    // First try current collection
    if (currentCollection && currentCollection.name) {
      return currentCollection.name;
    }

    // Then try to find in stored collections data
    if (fallbackId) {
      const storedCollection = getCollectionData(fallbackId);
      if (storedCollection && storedCollection.name) {
        return storedCollection.name;
      }
      return `Collection ${fallbackId}`;
    }

    return "Collection";
  };

  const value = {
    // Original methods
    currentCollection,
    setCollection,
    getCurrentCollection,
    clearCollection,
    hasCollection,
    getCollectionName,

    // New methods for collections data management
    collectionsData,
    setCollections,
    getCollectionData,
    updateCollectionData,
    fetchCollectionDetails,
    refreshCollections,
    loadCollectionsFromStorage,
  };

  return (
    <CollectionContext.Provider value={value}>
      {children}
    </CollectionContext.Provider>
  );
};

export default CollectionContext;
